import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import TestReportService, {
  TestReportDTO,
  TestReportResultDTO,
  UpsertRequest,
  MUUserItem,
} from "../../services/TestReport/TestReport.service";

import { generateTestReportPdf } from "./TestReportPdf";
import qaSeal from "../../assests/images/badge.png";
import centerLogo from "../../assests/images/DesiriLogo.png";

const formatDateOnly = (d?: Date | string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB") : "";

const fmtDynamic = (n: number) => {
  if (n == null) return "—";
  const s = n.toString();
  if (!s.includes(".")) return s;
  return s.replace(/\.0+$/, "").replace(/(\.[1-9]*)0+$/, "$1");
};

const normalizeStatus = (s?: string | null) => {
  const t = (s ?? "").trim().toUpperCase();
  return t || "—";
};

const parseNum = (val: unknown): number | null => {
  const t = (val ?? "").toString().trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const cleanDecimal = (s: string) =>
  (s ?? "").toString().replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

const simplifyOilName = (oilName?: string | null): string => {
  if (!oilName) return "—";
  const raw = oilName.trim();
  const up = raw.toUpperCase();
  const cleaned = raw.replace(/[()]/g, "").replace(/[,;]+/g, " ").trim();

  if (up.includes("ALMOND")) return "Almond Oil";
  if (up.includes("COCONUT")) return "Coconut Oil";
  if (up.includes("GROUNDNUT") || up.includes("PEANUT")) return "Groundnut Oil";
  if (up.includes("CASTOR")) return "Castor Oil";
  if (up.includes("SESAME")) return "Sesame Oil";
  if (up.includes("SUNFLOWER")) return "Sunflower Oil";
  if (up.includes("RICE BRAN") || up.includes("RICEBRAN")) return "Rice Bran Oil";
  if (up.includes("OLIVE")) return "Olive Oil";
  if (up.includes("FLAXSEED") || up.includes("LINSEED")) return "Flaxseed Oil";
  if (up.includes("MUSTARD")) return "Mustard Oil";
  if (up.includes("NIGER")) return "Niger Oil";
  if (up.includes("SAFFLOWER")) return "Safflower Oil";

  if (/\bOIL\b/i.test(cleaned)) {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens[tokens.length - 1].toUpperCase() === "OIL") tokens.pop();
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
    return cleaned;
  }

  {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
  }

  return raw.length > 40 ? raw.slice(0, 37) + "..." : raw;
};

const CARD_BG = "#f8f9fa";
const CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e6edf3",
  borderRadius: 10,
  padding: "12px",
  marginBottom: 12,
  background: CARD_BG,
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
};

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const TITLE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "1.05rem",
  margin: 0,
  color: "#3c3a3aff",
};

const META_LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  color: "#145326",
  marginBottom: 6,
};

const META_VALUE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 16,
  color: "#145326",
};

const SAMPLE_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 18,
  padding: "12px 6px",
  background: "#ffffff",
};

const SAMPLE_LABEL_STYLE: React.CSSProperties = {
  minWidth: 340,
  color: "#374151",
  fontSize: 14,
};

const INPUT_STYLE: React.CSSProperties = {
  width: 140,
  height: 36,
  padding: "6px 10px",
  textAlign: "right",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: "rgba(239, 236, 236, 1)",
};

const DROPDOWN_BUTTON_STYLE: React.CSSProperties = {
  border: "1px solid #0a9130",
  borderRadius: 6,
  width: 40,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#ffffff",
};

type ToleranceRow = {
  testId: number;
  minTolerance: number | null;
  maxTolerance: number | null;
};

type FormErrors = {
  product?: string;
  batch?: string;
  tests?: string;
  mu?: string;
  tester?: string;
  testedDate?: string;
  sampleQty?: string;
  analysisStart?: string;
  analysisEnd?: string;
  times?: string;
  duplicate?: string;
};

const TestReportGenerate: React.FC = () => {
  const [reportDate] = useState<Date>(new Date());
  const [manufacturingUnits, setManufacturingUnits] = useState<any[]>([]);
  const [manufacturingUnitId, setManufacturingUnitId] = useState<number | null>(null);
  const [testedByOptions, setTestedByOptions] = useState<MUUserItem[]>([]);
  const [testedById, setTestedById] = useState<number | null>(null);

  const [testedDate, setTestedDate] = useState<Date | null>(new Date());
  const [sampleQty, setSampleQty] = useState<number | null>(null);

  const [analysisStart, setAnalysisStart] = useState<Date | null>(null);
  const [analysisEnd, setAnalysisEnd] = useState<Date | null>(null);

  const [oilTypes, setOilTypes] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [batchNo, setBatchNo] = useState<number | null>(null);

  const [report, setReport] = useState<TestReportDTO | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [calculatedResults, setCalculatedResults] = useState<TestReportResultDTO[]>([]);

  const [savingFinal, setSavingFinal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [autoCalcLoading, setAutoCalcLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showValidation, setShowValidation] = useState(false);

  const [tolerances, setTolerances] =
    useState<Map<number, { min: number | null; max: number | null }>>(
      () => new Map()
    );

  const [expandedTests, setExpandedTests] = useState<Set<number>>(new Set());
  const testRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Duplicate check state
  const [dupeChecking, setDupeChecking] = useState(false);
  const [dupeExists, setDupeExists] = useState(false);
  const [dupeMsg, setDupeMsg] = useState<string | null>(null);

  const headerRefs = {
    product: useRef<HTMLDivElement>(null),
    batch: useRef<HTMLDivElement>(null),
    mu: useRef<HTMLDivElement>(null),
    tester: useRef<HTMLDivElement>(null),
    testedDate: useRef<HTMLDivElement>(null),
    analysisStart: useRef<HTMLDivElement>(null),
    analysisEnd: useRef<HTMLDivElement>(null),
    sampleQty: useRef<HTMLDivElement>(null),
  } as const;
  type HeaderKey = keyof typeof headerRefs;

  const toast = useRef<Toast>(null);
  const show = (sev: "success" | "info" | "warn" | "error", title: string, msg: string) =>
    toast.current?.show({ severity: sev, summary: title, detail: msg, life: 2200 });

  const scrollAndFocus = (el?: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => (el.querySelector("input, button, textarea, [tabindex]") as HTMLElement | null)?.focus?.(), 120);
  };

  const getMuName = () =>
    (manufacturingUnits.find((m) => m.id === manufacturingUnitId) || {}).name ?? "—";
  const getTesterName = () =>
    testedByOptions.find((o) => o.id === testedById)?.name ?? "—";

  // Build company address lines from MU row
  const buildCompanyLinesFromMU = (mu: any): string[] => {
    const lines: string[] = [];
    const push = (s?: any) => {
      const t = (s ?? "").toString().trim();
      if (t) lines.push(t);
    };
    push(mu?.address1 ?? mu?.Address1);

    const a2 = mu?.address2 ?? mu?.Address2;
    if (a2) {
      const chunks = String(a2)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (chunks.length) chunks.forEach(push);
      else push(a2);
    }

    push(mu?.landmark ?? mu?.Landmark);

    const zip =
      (mu?.zipcode ?? mu?.Zipcode ?? mu?.pincode ?? mu?.PinCode ?? "").toString().trim();
    if (zip && !lines.some((l) => l.includes(zip))) push(zip);

    return lines;
  };

  // Lookups
  useEffect(() => {
    (async () => {
      setLoadingLookups(true);
      try {
        const [oilsRaw, mus] = await Promise.all([
          TestReportService.getOilTypes(),
          TestReportService.getManufacturingUnits(),
        ]);

        const oilsMapped = (oilsRaw ?? []).map((o: any) => {
          const original = o?.name ?? o?.label ?? o?.Name ?? "";
          return {
            ...(o ?? {}),
            id: o?.id ?? o?.value ?? o?.Id,
            name: simplifyOilName(original),
            originalName: original,
          };
        });

        const aliasManufacturingUnitName = (orig?: string | null, id?: number) => {
          const up = (orig ?? "").toUpperCase().replace(/\s+/g, " ").trim();
          if (id === 1 || up.includes("K R NAGAR") || up.includes("KR NAGAR")) return "Unit-1-KR";
          if (id === 2 || up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
          return orig ?? "";
        };

        const musMapped = (mus ?? []).map((m: any) => {
          const id = Number(m?.id ?? m?.value ?? m?.Id);
          const originalName = m?.name ?? m?.Name ?? m?.label ?? "";
          return {
            ...(m ?? {}),
            id,
            name: aliasManufacturingUnitName(originalName, id),
            originalName,
          };
        });

        setOilTypes(oilsMapped);
        setManufacturingUnits(musMapped);
      } catch (err) {
        console.error("Load failed", err);
        show("error", "Load failed", "Could not load lookups");
        setOilTypes([]);
        setManufacturingUnits([]);
      } finally {
        setLoadingLookups(false);
      }
    })();
  }, []);

  // Load testers on MU change
  useEffect(() => {
    let alive = true;
    (async () => {
      setTestedByOptions([]);
      setTestedById(null);
      if (!manufacturingUnitId) return;

      try {
        const posId = Number(manufacturingUnitId);
        const testers = await TestReportService.getOilTestersByManufacturingUnit(posId);

        if (!alive) return;
        setTestedByOptions(testers ?? []);
      } catch (e) {
        if (!alive) return;
        show("error", "Load failed", `Could not load testers for POS #${manufacturingUnitId}`);
      }
    })();
    return () => {
      alive = false;
    };
  }, [manufacturingUnitId]);

  // Load tolerances
  const loadTolerances = async (productId: number) => {
    try {
      const rows: ToleranceRow[] = await TestReportService.getTolerances(productId);
      const map = new Map<number, { min: number | null; max: number | null }>();
      (rows ?? []).forEach((r) => map.set(r.testId, { min: r.minTolerance, max: r.maxTolerance }));
      setTolerances(map);
    } catch {
      setTolerances(new Map());
    }
  };

  const triggerPreview = (values?: Record<string, string>) => {
    if (!selectedProductId) return;
    if (autoCalcLoading) return;
    setAutoCalcLoading(true);

    const obj: Record<string, number> = {};
    Object.entries(values ?? inputValues).forEach(([k, v]) => {
      const num = parseNum(v);
      if (num !== null) obj[k] = num;
    });

    TestReportService.calculateResultsPreviewForProduct(selectedProductId, obj)
      .then((res) => setCalculatedResults(res ?? []))
      .catch(() => {})
      .finally(() => setAutoCalcLoading(false));
  };

  // Load template + tolerances on product change
  useEffect(() => {
    (async () => {
      setShowValidation(false);
      setFormErrors({});
      setReportId(null);
      setCalculatedResults([]);
      setInputValues({});
      setReport(null);

      if (!selectedProductId) return;

      try {
        const tpl = await TestReportService.getTemplateForProduct(selectedProductId);
        setReport(tpl);
        await loadTolerances(selectedProductId);

        const initial: Record<string, string> = {};
        for (const sample of tpl?.samples ?? []) {
          for (const r of sample.results ?? []) {
            if (r.value != null) initial[`${r.testId}:${sample.sampleId}`] = String(r.value);
          }
        }
        setInputValues(initial);
        triggerPreview(initial);
      } catch (err) {
        console.error("Failed to load template", err);
      }
    })();
  }, [selectedProductId]);

  const tests = useMemo(() => {
    if (!report) return [];
    const map: Record<
      number,
      { testId: number; testName: string; rows: Array<{ sampleId: number; sampleName: string }>; keys: string[] }
    > = {};
    for (const sample of report.samples ?? []) {
      for (const r of sample.results ?? []) {
        if (!map[r.testId]) {
          map[r.testId] = { testId: r.testId, testName: r.testName, rows: [], keys: [] };
        }
        map[r.testId].rows.push({ sampleId: sample.sampleId, sampleName: sample.sampleName });
        map[r.testId].keys.push(`${r.testId}:${sample.sampleId}`);
      }
    }
    return Object.values(map).sort((a, b) => a.testName.localeCompare(b.testName));
  }, [report]);

  const completeByTestId = useMemo(() => {
    const m = new Map<number, boolean>();
    tests.forEach((t) => {
      const allFilled = t.keys.every((k) => parseNum(inputValues[k]) !== null);
      m.set(t.testId, allFilled);
    });
    return m;
  }, [tests, inputValues]);

  const missingCells = useMemo(() => {
    if (!report) return [] as { key: string; testId: number; sampleId: number }[];
    const miss: { key: string; testId: number; sampleId: number }[] = [];
    for (const sample of report.samples) {
      for (const r of sample.results) {
        const key = `${r.testId}:${sample.sampleId}`;
        if (parseNum(inputValues[key]) === null) miss.push({ key, testId: r.testId, sampleId: sample.sampleId });
      }
    }
    return miss;
  }, [report, inputValues]);

  // Keep time errors synced
  useEffect(() => {
    if (!showValidation) return;
    setFormErrors((prev) => {
      const next = { ...prev };
      if (analysisStart) delete next.analysisStart;
      if (analysisEnd) delete next.analysisEnd;
      if (analysisStart && analysisEnd && analysisEnd >= analysisStart) delete next.times;
      return next;
    });
  }, [analysisStart, analysisEnd, showValidation]);

  useEffect(() => {
    if (analysisStart && analysisEnd && analysisEnd <= analysisStart) {
      setAnalysisEnd(null);
      show("info", "End time cleared", "Start moved after End. Please select a new end time.");
    }
  }, [analysisStart]);

  // Duplicate check effect (productId + batchNo must be unique)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setDupeChecking(true);
      setDupeExists(false);
      setDupeMsg(null);

      if (!selectedProductId || !batchNo) {
        setDupeChecking(false);
        return;
      }

      try {
        // debounce to reduce requests
        await new Promise((r) => setTimeout(r, 250));

        const exists = await TestReportService.existsReportForProductBatch(
          selectedProductId,
          batchNo,
          reportId ?? null
        );

        if (cancelled) return;

        const msg =
          exists ? "This batch number already exists for the selected oil. Please use different batch no." : null;

        setDupeExists(exists);
        setDupeMsg(msg);

        setFormErrors((prev) => {
          const next = { ...prev };
          if (exists && msg) next.duplicate = msg;
          else delete next.duplicate;
          return next;
        });
      } catch {
        if (!cancelled) {
          setDupeExists(false);
          setDupeMsg(null);
        }
      } finally {
        if (!cancelled) setDupeChecking(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedProductId, batchNo, reportId]);

  const validateBeforeSave = async (): Promise<boolean> => {
    const errors: FormErrors = {};
    if (!selectedProductId) errors.product = "Please select Oil Type";
    if (!batchNo) errors.batch = "Please enter Batch No";
    if (!manufacturingUnitId) errors.mu = "Please select Manufacturing Unit";
    if (!testedById) errors.tester = "Please select Tested By";
    if (!testedDate) errors.testedDate = "Please choose Tested Date";

    if (!analysisStart) errors.analysisStart = "Please set Analysis Start time";
    if (!analysisEnd) errors.analysisEnd = "Please set Analysis End time";
    if (analysisStart && analysisEnd && analysisEnd < analysisStart) {
      errors.times = "Analysis End cannot be before Analysis Start";
    }

    if (sampleQty == null || !Number.isInteger(sampleQty) || sampleQty <= 0) {
      errors.sampleQty = "Enter a valid Sample Quantity";
    }
    if (missingCells.length > 0) errors.tests = "Please enter values for all tests";

    // Strong duplicate check just before save
    if (selectedProductId && batchNo) {
      const exists = await TestReportService.existsReportForProductBatch(
        selectedProductId,
        batchNo,
        reportId ?? null
      );
      if (exists) {
        errors.duplicate =
          "This batch number is already used for the selected oil. Please use a different batch or pick a different oil.";
      }
    }

    setFormErrors(errors);
    setShowValidation(true);

    // Scroll to first error
    const order: Array<HeaderKey | "times" | "tests" | "duplicate"> = [
      "product",
      "batch",
      "mu",
      "tester",
      "testedDate",
      "sampleQty",
      "analysisStart",
      "analysisEnd",
      "times",
      "duplicate",
      "tests",
    ];
    for (const k of order) {
      if (k === "tests" && errors.tests) {
        const first = missingCells[0];
        setExpandedTests((prev) => new Set(prev).add(first.testId));
        setTimeout(() => scrollAndFocus(testRefs.current[first.testId]), 150);
        break;
      } else if (k === "times" && errors.times) {
        scrollAndFocus(headerRefs.analysisStart.current);
        break;
      } else if (k === "duplicate" && errors.duplicate) {
        scrollAndFocus(headerRefs.batch.current);
        break;
      } else if (k !== "tests" && k !== "times" && (errors as any)[k]) {
        scrollAndFocus(headerRefs[k as HeaderKey].current);
        break;
      }
    }

    return Object.keys(errors).length === 0;
  };

  const onChangeResult = (testId: number, sampleId: number, v: string) => {
    const key = `${testId}:${sampleId}`;
    setInputValues((prev) => {
      const next = { ...prev, [key]: v };
      triggerPreview(next);
      return next;
    });

    if (showValidation && parseNum(v) !== null) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.tests;
        return next;
      });
    }
  };

  const handleSaveFinal = async () => {
    const ok = await validateBeforeSave();
    if (!ok) return;

    setSavingFinal(true);
    try {
      const inputsObj: Record<string, number> = {};
      Object.entries(inputValues).forEach(([k, v]) => {
        const n = parseNum(v);
        if (n !== null) inputsObj[k] = n;
      });

      const body: UpsertRequest = {
        reportId,
        productId: selectedProductId!,
        batchNo: batchNo!,
        sampleQty,
        testedDate,
        analysisStart,
        analysisEnd,
        inputs: inputsObj,
        manufacturingUnitId,
        testedByUserId: testedById,
      };

      const resp = await TestReportService.upsertReportWithResults(body);
      setReport(resp.report);
      setReportId(resp.report.id);
      setBatchNo(resp.report.batchNo);
      setCalculatedResults(resp.results ?? []);

      setManufacturingUnitId(resp.report.manufacturingUnitId ?? manufacturingUnitId ?? null);
      setTestedById(resp.report.testedByUserId ?? testedById ?? null);
      setAnalysisStart(resp.report.analysisStartDate ? new Date(resp.report.analysisStartDate) : analysisStart);
      setAnalysisEnd(resp.report.analysisEndDate ? new Date(resp.report.analysisEndDate) : analysisEnd);
      setSampleQty(resp.report.sampleQty ?? sampleQty ?? null);

      setShowValidation(false);
      setFormErrors({});
      show("success", "Saved", "Saved successfully");
    } catch (err: any) {
      console.error("UPSERT ERROR", err?.response?.data ?? err);
      show("error", "Error", "Save failed");
    } finally {
      setSavingFinal(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!report || !reportId) {
      show("warn", "Save first", "Please save the report before generating the PDF.");
      return;
    }

    if (missingCells.length > 0) {
      setShowValidation(true);
      setFormErrors((prev) => ({ ...prev, tests: "Please enter values for all tests" }));
      const first = missingCells[0];
      setExpandedTests((prev) => new Set(prev).add(first.testId));
      setTimeout(() => scrollAndFocus(testRefs.current[first.testId]), 120);
      return;
    }

    setPdfLoading(true);
    try {
      if (!calculatedResults || calculatedResults.length === 0) {
        const obj: Record<string, number> = {};
        Object.entries(inputValues).forEach(([k, v]) => {
          const n = Number(v);
          if (Number.isFinite(n)) obj[k] = n;
        });
        const preview = await TestReportService.calculateResultsPreviewForProduct(
          report.productId,
          obj
        );
        setCalculatedResults(preview ?? []);
      }

      const muRow = manufacturingUnits.find((m) => Number(m.id) === Number(manufacturingUnitId));
      const companyLines = muRow ? buildCompanyLinesFromMU(muRow) : undefined;

      const meta = {
        reportId: report.id,
        reportDate,
        manufacturingUnitName: getMuName(),
        testedDate,
        analysisStart,
        analysisEnd,
        sampleQty: sampleQty ?? null,
        sampleQtyUnit: "ml",
        testerName: getTesterName(),
        oilName: report.productName,
        batchName: String(report.batchNo),
        companyLines,
      };

      await generateTestReportPdf({
        meta,
        results: calculatedResults,
        tolerances,
        centerLogoUrl: centerLogo,
        qaSealUrl: qaSeal,
        filename: `TestReport_${report.batchNo}_${report.productName}.pdf`,
      });

      show("success", "PDF ready", "Your test report PDF has been generated.");
    } catch (err) {
      console.error("PDF ERROR", err);
      show("error", "PDF error", "Could not generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const rangeText = (testId: number) => {
    const tol = tolerances.get(testId);
    if (!tol) return "—";
    const { min, max } = tol;
    if (min != null && max != null) return `${fmtDynamic(min)} – ${fmtDynamic(max)}`;
    if (min != null) return `≥ ${fmtDynamic(min)}`;
    if (max != null) return `≤ ${fmtDynamic(max)}`;
    return "N.S.";
  };

  const toggleExpand = (testId: number) => {
    setExpandedTests((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <Card title="Generate Test Report" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div style={{ maxWidth: 170 }}>
            <label className="block text-sm font-medium mb-1">
              Report Date <span className="text-red-600">*</span>
            </label>
            <InputText value={formatDateOnly(reportDate)} readOnly className="w-full" />
          </div>

          <div ref={headerRefs.mu}>
            <label className="block text-sm font-medium mb-1">
              Manufacturing Unit <span className="text-red-600">*</span>
            </label>
            <Dropdown
              value={manufacturingUnitId}
              options={manufacturingUnits}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setManufacturingUnitId(e.value)}
              placeholder="Select manufacturing unit"
              className={`w-full ${showValidation && formErrors.mu ? "p-invalid" : ""}`}
              disabled={loadingLookups}
            />
          </div>

          <div ref={headerRefs.tester}>
            <label className="block text-sm font-medium mb-1">
              Tested By <span className="text-red-600">*</span>
            </label>
            <Dropdown
              value={testedById}
              options={testedByOptions}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setTestedById(e.value)}
              placeholder={
                !manufacturingUnitId
                  ? "(select unit first)"
                  : testedByOptions.length === 0
                  ? "No Oil Testers in this unit"
                  : "Select tester"
              }
              className={`w-full ${showValidation && formErrors.tester ? "p-invalid" : ""}`}
              disabled={!manufacturingUnitId || testedByOptions.length === 0}
              showClear
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.testedDate}>
            <label className="block text-sm font-medium mb-1">
              Tested Date <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={testedDate}
              onChange={(e) => setTestedDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              showIcon
              className={showValidation && formErrors.testedDate ? "p-invalid" : undefined}
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.analysisStart}>
            <label className="block text-sm font-medium mb-1">
              Analysis Start <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={analysisStart}
              onFocus={() => {
                if (!analysisStart) setAnalysisStart(new Date());
              }}
              onChange={(e) => {
                const selected = e.value as Date;
                if (!selected) return;
                setAnalysisStart(selected);
                if (analysisEnd && analysisEnd <= selected) {
                  setAnalysisEnd(null);
                  show("info", "End time cleared", "Start moved after End. Please select a new end time.");
                }
              }}
              timeOnly
              hourFormat="12"
              stepMinute={1}
              showIcon
              icon="pi pi-clock"
              className={
                showValidation && (formErrors.analysisStart || formErrors.times) ? "p-invalid" : undefined
              }
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.analysisEnd}>
            <label className="block text-sm font-medium mb-1">
              Analysis End <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={analysisEnd}
              onChange={(e) => {
                const selected = e.value as Date;
                if (!selected) {
                  setAnalysisEnd(null);
                  return;
                }
                if (analysisStart && selected < analysisStart) {
                  show("warn", "Invalid time", "End time cannot be earlier than start time.");
                  return;
                }
                setAnalysisEnd(selected);
              }}
              timeOnly
              hourFormat="12"
              stepMinute={1}
              showIcon
              icon="pi pi-clock"
              minDate={analysisStart ?? undefined}
              className={
                showValidation && (formErrors.analysisEnd || formErrors.times) ? "p-invalid" : undefined
              }
            />
          </div>

          <div style={{ maxWidth: 180 }} ref={headerRefs.sampleQty}>
            <label className="block text-sm font-medium mb-1">
              Sample Quantity <span className="text-red-600">*</span>
            </label>
            <div className="p-inputgroup">
              <InputNumber
                value={sampleQty}
                onValueChange={(e) => {
                  const v = typeof e.value === "number" ? Math.max(1, Math.floor(e.value)) : null;
                  setSampleQty(v);
                  if (showValidation) {
                    setFormErrors((prev) => {
                      const n = { ...prev };
                      if (v && Number.isInteger(v) && v > 0) delete n.sampleQty;
                      return n;
                    });
                  }
                }}
                mode="decimal"
                min={1}
                useGrouping={false}
                maxFractionDigits={0}
                className={`w-full ${showValidation && formErrors.sampleQty ? "p-invalid" : ""}`}
              />
              <span className="p-inputgroup-addon">ml</span>
            </div>
            {showValidation && formErrors.sampleQty && (
              <small className="p-error block mt-1">{formErrors.sampleQty}</small>
            )}
          </div>

          <div style={{ maxWidth: 140 }} ref={headerRefs.batch}>
            <label className="block text-sm font-medium mb-1">
              Batch No <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={batchNo ?? ""}
              onInput={(e) => {
                const v = (e.target as HTMLInputElement).value.replace(/[^\d]/g, "");
                (e.target as HTMLInputElement).value = v;
                const next = v === "" ? null : Math.max(1, Number(v));
                setBatchNo(next);
                setReportId(null);
                if (showValidation) {
                  setFormErrors((prev) => {
                    const n = { ...prev };
                    if (next) delete n.batch;
                    return n;
                  });
                }
              }}
              placeholder="Enter batch no"
              className={`border rounded p-2 w-full ${showValidation && (formErrors.batch || formErrors.duplicate) ? "p-invalid" : ""}`}
              aria-invalid={!!(showValidation && (formErrors.batch || formErrors.duplicate))}
              aria-describedby="batchNo-error"
            />
            {(showValidation && formErrors.batch) && (
              <small id="batchNo-error" className="text-red-600 text-sm mt-1 block">
                {formErrors.batch}
              </small>
            )}
            {dupeExists && (
              <small className="text-red-600 text-sm mt-1 block">
                {dupeMsg ?? "This batch number is already used for the selected oil."}
              </small>
            )}
          </div>
        </div>
      </Card>

      <Card title="Select Oil Type" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-1" ref={headerRefs.product}>
            <label className="block text-sm font-medium mb-1">
              Oil Type <span className="text-red-600">*</span>
            </label>
            <Dropdown
              value={selectedProductId}
              options={oilTypes}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setSelectedProductId(e.value)}
              placeholder="Select oil type"
              className={`w-full ${showValidation && formErrors.product ? "p-invalid" : ""}`}
              disabled={loadingLookups}
              showClear
              filter
              filterBy="name,originalName"
              filterPlaceholder="Search oils..."
              filterMatchMode="startsWith"
            />
            {showValidation && formErrors.product && (
              <small className="p-error"> {formErrors.product} </small>
            )}
            {/* Note: duplicate message is intentionally NOT shown here */}
          </div>
        </div>
      </Card>

      {/* Hide tests/inputs section while duplicate exists */}
      {report && !dupeExists && (
        <Card title={simplifyOilName(report.productName).toUpperCase()} style={{ paddingBottom: 12 }}>
          {showValidation && formErrors.tests && (
            <div className="text-red-600 text-sm mb-2">{formErrors.tests}</div>
          )}

          <div style={{ maxHeight: "62vh", overflowY: "auto", paddingRight: 8 }}>
            {tests.map((t) => {
              const isComplete = completeByTestId.get(t.testId) === true;
              const previewRow = calculatedResults?.find((r) => r.testId === t.testId);
              const isOpen = expandedTests.has(t.testId);

              return (
                <div
                  key={t.testId}
                  ref={(el) => (testRefs.current[t.testId] = el)}
                  style={CARD_STYLE}
                >
                  <div style={HEADER_STYLE}>
                    <div style={{ ...TITLE_STYLE }}>{t.testName}</div>

                    <div style={{ marginLeft: "auto" }}>
                      <Button
                        icon={isOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                        onClick={() => toggleExpand(t.testId)}
                        className="p-button-text"
                        style={DROPDOWN_BUTTON_STYLE}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "12px",
                      background: CARD_BG,
                      borderTop: "1px solid #eef2f6",
                      display: "flex",
                      gap: 24,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 220 }}>
                      <div style={META_LABEL_STYLE}>Result</div>
                      <div style={META_VALUE_STYLE}>
                        {isComplete && previewRow && previewRow.result != null
                          ? Number(previewRow.result).toFixed(4)
                          : isComplete && autoCalcLoading
                          ? "…"
                          : "—"}
                      </div>
                    </div>

                    <div style={{ minWidth: 160 }}>
                      <div style={META_LABEL_STYLE}>Status</div>
                      <div style={{ marginTop: 6 }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "6px 10px",
                            borderRadius: 6,
                            color: "#fff",
                            background:
                              isComplete && previewRow?.status === "PASS"
                                ? "#16a34a"
                                : isComplete && previewRow?.status === "FAIL"
                                ? "#dc2626"
                                : "#6b7280",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {isComplete && previewRow ? normalizeStatus(previewRow.status) : "—"}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginLeft: "auto", textAlign: "right", minWidth: 240 }}>
                      <div style={META_LABEL_STYLE}>Acceptable Range</div>
                      <div style={{ fontWeight: 600, color: "#145326" }}>{rangeText(t.testId)}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div>
                      {(t.rows || []).map((r, idx) => {
                        const key = `${t.testId}:${r.sampleId}`;
                        const currentVal = inputValues[key] ?? "";
                        const isMissing = showValidation && parseNum(currentVal) === null;
                        const showBorder = (t.rows?.length ?? 0) > 1 && idx < (t.rows?.length ?? 0) - 1;
                        const rowStyle = {
                          ...SAMPLE_ROW_STYLE,
                          borderBottom: showBorder ? "1px solid #c2b9b9ff" : "none",
                        };

                        return (
                          <div key={key} style={rowStyle}>
                            <div style={SAMPLE_LABEL_STYLE}>{r.sampleName}</div>

                            <div style={{ marginLeft: "auto" }}>
                              <InputText
                                name={key}
                                value={currentVal}
                                onInput={(e) => {
                                  const raw = (e.target as HTMLInputElement).value;
                                  const cleaned = cleanDecimal(raw);
                                  if (cleaned !== raw) (e.target as HTMLInputElement).value = cleaned;
                                  onChangeResult(t.testId, r.sampleId, cleaned);
                                }}
                                placeholder="0"
                                inputMode="decimal"
                                style={INPUT_STYLE}
                              />
                              {isMissing && (
                                <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>Required</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-3" style={{ justifyContent: "flex-end" }}>
            <Button
              label="Save"
              icon="pi pi-check-circle"
              severity="success"
              onClick={handleSaveFinal}
              loading={savingFinal}
              disabled={dupeExists || dupeChecking}
            />
            <Button
              label="Generate PDF"
              icon="pi pi-file-pdf"
              className="p-button-danger"
              onClick={handleGeneratePdf}
              loading={pdfLoading}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default TestReportGenerate;